// API utilities for the policy widget. One payload shape everywhere (SMA-86): the JSON:API
// document the public API and the directory-scoped policy endpoint render, and the hosted
// page inlines (SMA-76).
import { readPublicDocument, unavailable } from '~/widgets/read'

function findIncluded(included, type, id) {
  if (!included || !id) return null
  return included.find(item => item.type === type && item.id === id)
}

/**
 * Normalize the JSON:API policy document to the flat shape the component reads.
 */
export function normalizePolicy(response) {
  const { data, included } = response
  const attrs = data.attributes

  const orgRef = data.relationships?.organization?.data
  const org = orgRef ? findIncluded(included, orgRef.type, orgRef.id) : null

  return {
    id: data.id,
    name: attrs.name,
    shortcode: attrs.shortcode,
    short_description: attrs.short_description,
    point_of_contact: attrs.point_of_contact,
    cvd_timeline: attrs.cvd_timeline,
    safe_harbor: attrs.safe_harbor,
    offers_bounty: attrs.offers_bounty,
    offers_swag: attrs.offers_swag,
    hall_of_fame: attrs.hall_of_fame,
    public_disclosure: attrs.public_disclosure,
    safe_harbor_attested_on: attrs.safe_harbor_attested_on,
    disclosure_attested_on: attrs.disclosure_attested_on,
    rewards_attested_on: attrs.rewards_attested_on,
    // Server-rendered, sanitized HTML (kept under the name the component has always read).
    policy_markdown: attrs.policy_html,
    public_url: attrs.public_url,
    // Server-decided (SMA-76): false only when the plan includes white label.
    attribution: attrs.attribution,
    updated_at: attrs.updated_at,
    organization: org ? { name: org.attributes.name } : null,
    template: attrs.template,
    branding: attrs.branding
  }
}

/**
 * Fetch a policy. A 404 is a policy that is not published (or not addressable where it was
 * asked for) and a 403 upgrade_required is one whose plan does not include embeds; both come
 * back as `available: false` with a `reason`. Any other failure is an error.
 */
export async function fetchPolicy(dataUrl) {
  const read = await readPublicDocument(dataUrl)
  if (read.status !== 'ok') return unavailable(read)
  return normalizePolicy(read.document)
}
